import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const scanLine = keyframes`
  0% { transform: translateY(-100vh); }
  100% { transform: translateY(100vh); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(1deg); }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.8; }
`;

const GridContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
  z-index: 0;
`;

const ScanLine = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, transparent, var(--primary), transparent);
  animation: ${scanLine} 4s linear infinite;
  opacity: 0.5;
  z-index: 1;
  pointer-events: none;
`;

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(10, 10, 15, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-color);
  z-index: 100;
`;

const Logo = styled(Link)`
  font-family: 'Orbitron', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: var(--primary);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 10px;
  
  span {
    background: linear-gradient(135deg, var(--primary), var(--accent));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  &:hover {
    animation: ${glow} 1s ease-in-out infinite;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const UserName = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
  
  .admin-badge {
    background: linear-gradient(135deg, var(--primary), var(--accent));
    color: #000;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: bold;
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-family: 'Orbitron', sans-serif;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: rgba(0, 240, 255, 0.1);
  }
`;

const NavLink = styled(Link)`
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 2px;
  position: relative;
  padding: 8px 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--primary), var(--accent));
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: var(--primary);
    
    &::after {
      width: 100%;
    }
  }
  
  &.active {
    color: var(--primary);
    
    &::after {
      width: 100%;
    }
  }
`;

const MainContent = styled.main`
  padding-top: 100px;
  min-height: 100vh;
  position: relative;
  z-index: 10;
`;

const HeroSection = styled.section`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  position: relative;
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(36px, 8vw, 80px);
  font-weight: 900;
  background: linear-gradient(135deg, #ffffff 0%, var(--primary) 50%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 20px;
  text-shadow: 0 0 60px rgba(0, 240, 255, 0.5);
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(16px, 3vw, 24px);
  color: var(--text-secondary);
  max-width: 700px;
  margin-bottom: 50px;
  line-height: 1.8;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  width: 100%;
  padding: 40px 20px;
`;

const FeatureCard = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 40px 30px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, var(--primary), var(--accent));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  display: inline-block;
  animation: ${float} 3s ease-in-out infinite;
`;

const FeatureTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 20px;
  color: var(--primary);
  margin-bottom: 15px;
`;

const FeatureDesc = styled.p`
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.7;
`;

const CTABtn = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: #fff;
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 40px;
  border-radius: 30px;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 10px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    box-shadow: 0 0 30px rgba(0, 240, 255, 0.6);
  }
`;

const SecondaryBtn = styled(CTABtn)`
  background: transparent;
  border: 2px solid var(--primary);
  
  &:hover {
    background: rgba(0, 240, 255, 0.1);
  }
`;

const TechStack = styled.div`
  display: flex;
  gap: 40px;
  margin-top: 60px;
  flex-wrap: wrap;
  justify-content: center;
`;

const TechItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 14px;
  
  span:first-child {
    font-size: 32px;
  }
`;

export {
  GridContainer,
  ScanLine,
  NavbarContainer,
  Logo,
  NavLinks,
  NavLink,
  MainContent,
  HeroSection,
  HeroTitle,
  HeroSubtitle,
  FeatureGrid,
  FeatureCard,
  FeatureIcon,
  FeatureTitle,
  FeatureDesc,
  CTABtn,
  SecondaryBtn,
  TechStack,
  TechItem,
  UserInfo,
  UserName,
  LogoutButton
};
